import { useState, useEffect } from "react";
import API from "@/lib/api";
import { Headphones, Loader2, Check, X, RefreshCw } from "lucide-react";

const STATUS_BADGE = { open: "bg-amber-100 text-amber-700", resolved: "bg-green-100 text-green-700", closed: "bg-gray-100 text-gray-600" };

export default function AdminComplaints() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resyncing, setResyncing] = useState({}); // id -> bool
  const [resyncMsg, setResyncMsg] = useState({}); // id -> { ok, text }

  useEffect(() => { loadItems(); }, []);
  const loadItems = () => { API.get("/admin/complaints").then(r => setItems(r.data || [])).catch(() => {}).finally(() => setLoading(false)); };

  const updateStatus = async (id, status, resolution = "") => {
    await API.put(`/admin/complaints/${id}`, { status, resolution });
    loadItems();
  };

  const resyncZoho = async (id) => {
    setResyncing(prev => ({ ...prev, [id]: true }));
    setResyncMsg(prev => ({ ...prev, [id]: null }));
    try {
      const { data } = await API.post(`/admin/complaints/${id}/zoho-resync`);
      setResyncMsg(prev => ({ ...prev, [id]: { ok: !!data.ok, text: data.ok ? `Zoho ${data.mode} succeeded` : `Zoho ${data.mode} failed` } }));
      loadItems();
    } catch (e) {
      setResyncMsg(prev => ({ ...prev, [id]: { ok: false, text: e.response?.data?.detail || "Resync failed" } }));
    } finally {
      setResyncing(prev => ({ ...prev, [id]: false }));
      setTimeout(() => setResyncMsg(prev => ({ ...prev, [id]: null })), 4000);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-complaints">
      <div className="flex items-center gap-3">
        <Headphones className="w-6 h-6 text-[#008A99]" />
        <div><h1 className="text-2xl font-bold text-gray-900">Complaints / Service Requests</h1><p className="text-sm text-gray-500">{items.length} total</p></div>
      </div>
      {loading ? <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#008A99] mx-auto" /></div> : (
        <div className="space-y-3">
          {items.map(item => (
            <div key={item.id} className="bg-white border rounded-xl p-4" data-testid="admin-complaint-item">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-[#008A99] text-sm">{item.sr_number}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_BADGE[item.status] || STATUS_BADGE.open}`}>{item.status}</span>
                    {item.zoho_sr_number && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 font-mono" data-testid={`zoho-sr-${item.sr_number}`}>
                        Zoho · {item.zoho_sr_number}
                      </span>
                    )}
                    {item.complaint_type && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-semibold">
                        {item.complaint_type}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400">{item.user_name} | {item.user_mobile} | Serial: {item.instrument_serial}</p>
                  {item.reagent_code && <p className="text-xs text-gray-400">Reagent: {item.reagent_code}{item.lot_number ? ` · Lot ${item.lot_number}` : ""}</p>}
                </div>
                {item.status === "open" && (
                  <button onClick={() => updateStatus(item.id, "resolved", "Resolved by service team")}
                    className="px-3 py-1.5 bg-green-50 text-green-600 rounded-lg text-xs font-semibold hover:bg-green-100">Mark Resolved</button>
                )}
              </div>
              <p className="text-sm text-gray-700">{item.issue}</p>
              {item.reagent_code && (
                <div className="flex items-center justify-between gap-2 mt-2 pt-2 border-t border-gray-100">
                  <span className="text-[10px] text-gray-400">
                    {item.zoho_record_id ? `Zoho record #${item.zoho_record_id}` : "Not synced to Zoho yet"}
                  </span>
                  <div className="flex items-center gap-2">
                    {resyncMsg[item.id] && (
                      <span className={`text-[10px] font-semibold ${resyncMsg[item.id].ok ? "text-emerald-600" : "text-red-600"}`} data-testid={`zoho-resync-msg-${item.id}`}>
                        {resyncMsg[item.id].text}
                      </span>
                    )}
                    <button
                      onClick={() => resyncZoho(item.id)}
                      disabled={!!resyncing[item.id]}
                      className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-bold rounded-lg bg-violet-50 text-violet-700 hover:bg-violet-100 disabled:opacity-50"
                      data-testid={`zoho-resync-btn-${item.id}`}
                      title={item.zoho_record_id ? "Push current state to Zoho" : "Create SR on Zoho"}
                    >
                      {resyncing[item.id] ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                      {item.zoho_record_id ? "Resync to Zoho" : "Send to Zoho"}
                    </button>
                  </div>
                </div>
              )}
              {item.customer_feedback && (
                <p className="text-xs text-sky-700 bg-sky-50 p-2 rounded-lg mt-2"><span className="font-bold">Zoho feedback:</span> {item.customer_feedback}</p>
              )}
              {item.closure_remark && (
                <p className="text-xs text-amber-700 bg-amber-50 p-2 rounded-lg mt-2"><span className="font-bold">Closure:</span> {item.closure_remark}</p>
              )}
              <p className="text-xs text-gray-400 mt-1">{new Date(item.created_at).toLocaleDateString()}</p>
            </div>
          ))}
          {items.length === 0 && <p className="text-center text-gray-400 py-8">No complaints yet</p>}
        </div>
      )}
    </div>
  );
}
